/**
 * src/hooks/useLiveBroadcasts.ts
 *
 * What is talking anywhere on the map, in one request.
 *
 * `useSpatialProximity` answers a different question — what can be heard
 * from where the visitor is standing — and it cannot answer this one. A
 * visitor in Dubai may hover a building in Las Vegas, and the hover card
 * still has to say whether there is anything to hear. Asking per object
 * would mean a request per building hovered, which is the cost
 * `spatialTooltipModel` exists to refuse.
 *
 * So this polls the whole set instead. `/spatial-broadcasts/active`
 * returns only sessions that are actually LIVE, and there are normally
 * none: the response is small, the request is one, and the answer covers
 * every object on screen at once.
 *
 * Nothing here starts a broadcast. Reading what is live must never be
 * the reason something becomes live.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import {
  listActiveSpatialBroadcasts,
  type SpatialBroadcastSession,
} from '../services/spatial/spatialPresenceClient';

/** The minimum a player needs to join, and a card needs to describe it. */
export interface LiveBroadcastInfo {
  objectId: string;
  /** Room key. `spatial:<uuid>`, as minted by spatialBroadcastService. */
  broadcasterId: string;
  sessionId: string;
  objectName: string | null;
  title: string | null;
  transport: string;
  viewerCount: number;
  countryCode: string | null;
}

/**
 * What a player needs from a session, or null if it cannot be joined.
 *
 * The single definition of "audible", used by the map-wide poll and by
 * the player card's own per-object read, so the two cannot disagree
 * about whether a visitor may listen.
 *
 * `STARTING` is excluded deliberately: a room that exists but has
 * nothing coming out of it yet would offer a visitor a player that sits
 * in silence, which reads as broken rather than as early. A session with
 * no room key is excluded for a blunter reason — there is nothing to
 * join, so saying otherwise is an offer we cannot honour.
 */
export function liveBroadcastInfo(
  session: SpatialBroadcastSession | null | undefined,
): LiveBroadcastInfo | null {
  if (!session || session.status !== 'LIVE') return null;

  const objectId = session.spatialObjectId ? String(session.spatialObjectId) : '';
  if (!objectId || !session.broadcasterId) return null;

  return {
    objectId,
    broadcasterId: String(session.broadcasterId),
    sessionId: String(session.id),
    objectName: session.objectName ?? null,
    title: session.title ?? null,
    transport: session.transport || 'signaling_only',
    viewerCount: Number(session.viewerCount) || 0,
    countryCode: session.countryCode ?? null,
  };
}

/**
 * Reduce the server's sessions to what the map needs, keyed by object.
 *
 * Pure and exported so the filtering can be tested without a timer or a
 * fetch.
 */
export function indexLiveBroadcasts(
  sessions: SpatialBroadcastSession[] | null | undefined,
): Map<string, LiveBroadcastInfo> {
  const out = new Map<string, LiveBroadcastInfo>();
  if (!Array.isArray(sessions)) return out;

  for (const session of sessions) {
    const info = liveBroadcastInfo(session);
    if (info) out.set(info.objectId, info);
  }
  return out;
}

export interface UseLiveBroadcastsResult {
  /**
   * Whether the answer is from the server rather than from an empty
   * initial state. "We have not asked" and "nothing is live" are
   * different claims and the hover card renders them differently.
   */
  ready: boolean;
  /** The live broadcast for an object, if it has one. */
  get: (objectId: string | null | undefined) => LiveBroadcastInfo | null;
  /** How many objects are live right now. */
  count: number;
  error: string | null;
  refresh: () => void;
}

const DEFAULT_INTERVAL_MS = 20_000;

export function useLiveBroadcasts(options: {
  enabled?: boolean;
  intervalMs?: number;
} = {}): UseLiveBroadcastsResult {
  const { enabled = true, intervalMs = DEFAULT_INTERVAL_MS } = options;

  const [index, setIndex] = useState<Map<string, LiveBroadcastInfo>>(() => new Map());
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const inFlightRef = useRef(false);
  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;

    const poll = async () => {
      if (inFlightRef.current) return;
      // A hidden tab cannot play audio anybody is listening to, so it
      // has no business asking who is speaking.
      if (typeof document !== 'undefined' && document.hidden) return;

      inFlightRef.current = true;
      try {
        const result = await listActiveSpatialBroadcasts();
        if (cancelled) return;

        if (!result.ok) {
          // Keep the last known set. A failed poll means we do not know
          // what changed, not that everything went quiet.
          setError(result.reason);
          return;
        }
        setError(null);
        setIndex(indexLiveBroadcasts(result.broadcasts));
        setReady(true);
      } finally {
        inFlightRef.current = false;
      }
    };

    void poll();
    const timer = setInterval(poll, Math.max(5_000, intervalMs));

    // Coming back to the tab should not wait out the rest of an interval
    // to find out that the building in front of you started talking.
    const onVisible = () => { if (!document.hidden) void poll(); };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisible);
    }

    return () => {
      cancelled = true;
      clearInterval(timer);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisible);
      }
    };
  }, [enabled, intervalMs, nonce]);

  const get = useCallback(
    (objectId: string | null | undefined) => (objectId ? index.get(objectId) ?? null : null),
    [index],
  );

  return { ready, get, count: index.size, error, refresh };
}

export default useLiveBroadcasts;
